import type { z } from 'zod';
import {
  apiResponseSchema,
  authTokensSchema,
  driverActionResultSchema,
  driverRouteTodaySchema,
} from './schemas';
import type {
  AuthTokens,
  DriverActionRequest,
  DriverActionResult,
  DriverRouteToday,
} from './types';
import { getApiBaseUrl } from '../lib/runtimeEnv';

/**
 * Basis-URL van `Gedenk.Api`, inclusief `/api/v1`. Bepaald bij module-load uit de
 * runtime-config (`/env.js`) met terugval op de build-time waarde — zie
 * `src/lib/runtimeEnv.ts` voor de precedentie.
 */
export const API_BASE_URL: string = getApiBaseUrl();

export class ApiError extends Error {
  readonly status: number;
  /** Stabiele foutcode uit `errors[0]` (contract §1.2); `ApiErrorCode` somt de bekende op. */
  readonly code: string;
  /** Bij de conflictcodes van contract §5.6 vult de server ook `data` met een verse snapshot. */
  readonly result: DriverActionResult | null;

  constructor(
    status: number,
    code: string,
    message: string,
    result: DriverActionResult | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.result = result;
  }

  /**
   * Contract §5.6 / README §Offline-first: 4xx behalve 401/408/429 is definitief mislukt.
   * Al het andere (netwerk, 5xx, tijdelijke codes) blijft in de wachtrij staan.
   * `IDEMPOTENCY_IN_FLIGHT` is de uitzondering binnen 409: dezelfde handeling is nog in
   * behandeling, dus opnieuw proberen met backoff — niet "lukt niet" tonen.
   */
  get isDefinitive(): boolean {
    if (this.status === 401 || this.status === 408 || this.status === 429) return false;
    if (this.code === 'IDEMPOTENCY_IN_FLIGHT') return false;
    return this.status >= 400 && this.status < 500;
  }
}

export interface SessionPort {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  onRefreshed(tokens: AuthTokens): void | Promise<void>;
  onSessionLost(reason: string): void | Promise<void>;
}

interface RequestOptions {
  method: 'GET' | 'POST';
  path: string;
  body?: unknown;
  auth: boolean;
  signal?: AbortSignal;
}

function firstCode(errors: unknown): string {
  if (Array.isArray(errors) && typeof errors[0] === 'string' && errors[0].length > 0) {
    return errors[0];
  }
  return 'UNKNOWN';
}

export class DriverApiClient {
  private readonly baseUrl: string;
  private readonly session: SessionPort;
  private refreshInFlight: Promise<AuthTokens | null> | null = null;

  constructor(session: SessionPort, baseUrl: string = API_BASE_URL) {
    this.session = session;
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  async login(email: string, password: string): Promise<AuthTokens> {
    const raw = await this.send({
      method: 'POST',
      path: '/driver/auth/login',
      body: { email, password },
      auth: false,
    });
    return this.unwrap(raw, authTokensSchema);
  }

  /** Ververst de sessie. `null` als er geen refresh token is. Contract §2.2. */
  async refresh(): Promise<AuthTokens | null> {
    this.refreshInFlight ??= this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<AuthTokens | null> {
    const refreshToken = this.session.getRefreshToken();
    if (!refreshToken) return null;
    try {
      const raw = await this.send({
        method: 'POST',
        path: '/driver/auth/refresh',
        body: { refreshToken },
        auth: false,
      });
      const tokens = this.unwrap(raw, authTokensSchema);
      await this.session.onRefreshed(tokens);
      return tokens;
    } catch (error) {
      // 401 op de refresh (verlopen, ingetrokken, of A-03 `REFRESH_TOKEN_SCOPE_MISMATCH`)
      // betekent: terug naar het inlogscherm. De wachtrij blijft nadrukkelijk staan.
      if (error instanceof ApiError && error.status === 401) {
        await this.session.onSessionLost(error.code);
      }
      throw error;
    }
  }

  async getRouteToday(signal?: AbortSignal): Promise<DriverRouteToday> {
    const raw = await this.authed({ method: 'GET', path: '/driver/route/today', auth: true, signal });
    return this.unwrap(raw, driverRouteTodaySchema);
  }

  async postAction(
    routeId: string,
    request: DriverActionRequest,
    signal?: AbortSignal,
  ): Promise<DriverActionResult> {
    const raw = await this.authed({
      method: 'POST',
      path: `/driver/route/${encodeURIComponent(routeId)}/actions`,
      body: request,
      auth: true,
      signal,
    });
    return this.unwrap(raw, driverActionResultSchema);
  }

  /** Eén verzoek, en bij 401 precies één keer verversen-en-opnieuw (contract §2.3). */
  private async authed(options: RequestOptions): Promise<unknown> {
    try {
      return await this.send(options);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      const tokens = await this.refresh();
      if (!tokens) throw error;
      return this.send(options);
    }
  }

  private async send(options: RequestOptions): Promise<unknown> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.auth) {
      const token = this.session.getAccessToken();
      // Het token gaat uitsluitend in de header — nooit in een URL (security-hardening).
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${options.path}`, {
        method: options.method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Geen verbinding met de server.', null);
    }

    let payload: unknown = null;
    const text = await response.text();
    if (text.length > 0) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }

    if (!response.ok) {
      const envelope = payload as { message?: unknown; errors?: unknown; data?: unknown } | null;
      const code = firstCode(envelope?.errors);
      const message =
        typeof envelope?.message === 'string' && envelope.message.length > 0
          ? envelope.message
          : 'Er ging iets mis bij de server.';
      const snapshot = driverActionResultSchema.safeParse(envelope?.data);
      throw new ApiError(response.status, code, message, snapshot.success ? snapshot.data : null);
    }

    return payload;
  }

  private unwrap<T>(raw: unknown, schema: z.ZodType<T>): T {
    const envelope = apiResponseSchema(schema).safeParse(raw);
    if (!envelope.success) {
      throw new ApiError(0, 'VALIDATION_FAILED', 'Het antwoord van de server klopt niet.', null);
    }
    if (!envelope.data.success || envelope.data.data === null) {
      throw new ApiError(
        0,
        firstCode(envelope.data.errors),
        envelope.data.message ?? 'Er ging iets mis bij de server.',
        null,
      );
    }
    return envelope.data.data;
  }
}
